import { observer } from 'mobx-react-lite';
import Image from 'next/legacy/image';
import React, { memo, useState } from 'react';

import { Modal } from '@/components/common/Modal/Modal';
import { useMediaQuery } from '@/hooks';
import type { OrderPlan } from '@/models/cart/order-plan';
import { useStore } from '@/models/root-store';
import { GOAL_COLLECTION_TYPE } from '@/utils/api-constants';
import { Mode } from '@/utils/constants';

type HeaderProps = {
  position?: string;
  orderPlan?: OrderPlan;
};

const Header: React.FC<HeaderProps> = observer(({ position, orderPlan }) => {
  const { cartStore } = useStore();
  const { draftCart } = cartStore;
  const cal = orderPlan?.findAttribute('calorie');
  const gender = orderPlan?.findAttribute('gender');
  const ageRange = orderPlan?.findAttribute('ageRange');
  const goal = orderPlan?.findAttribute('goal');
  const weight = orderPlan?.findAttribute('weight');
  const planType = GOAL_COLLECTION_TYPE[
    goal as keyof typeof GOAL_COLLECTION_TYPE
  ]?.replace('-', ' ');
  const hideAgeRange = orderPlan?.planRoute === '/meal-plan/';

  const [infoModal, setInfoModal] = useState(false);

  const isSmall = useMediaQuery('(max-width: 768px)');
  const imageDimensions = isSmall ? '84' : '112';

  return (
    <div>
      {draftCart.mode !== Mode.EDIT_SUBSCRIPTION ? (
        <div>
          <div
            className={`grid grid-cols-1 items-center rounded-t-xl p-3 md:columns-2 md:grid-cols-4 md:pb-0 lg:px-8 lg:pt-8 ${
              position === 'fixed' ? 'bg-white' : 'bg-rhubarb-lite'
            }`}
          >
            <div className="col-span-1 pb-3 font-interMedium text-2xl capitalize leading-7 text-rhubarb-dark md:col-span-2 md:pb-0 md:text-4xl lg:col-span-3 lg:text-black">
              My {planType} Plan
            </div>
            <div className="col-span-1 flex flex-wrap md:col-span-2 lg:col-span-1">
              <span className="pr-2 font-interMedium text-base capitalize leading-5">
                {gender?.toLowerCase()}
              </span>
              {ageRange && !hideAgeRange && (
                <span className="flex items-center pr-2 font-interMedium text-base">
                  <div className="mr-2 size-1 rounded-full bg-chef-bean">
                    {''}
                  </div>
                  <span className="leading-5">{ageRange} yrs old</span>
                </span>
              )}
              {weight && (
                <span className="flex items-center font-interMedium text-base">
                  <div className="mr-2 size-1 rounded-full bg-chef-bean">
                    {''}
                  </div>

                  <span className="leading-5">{weight}kg</span>
                </span>
              )}
            </div>
          </div>
          {cal ? (
            <div
              className={`grid grid-cols-1 items-center rounded-b-xl px-3 pb-3 pt-3.5 md:columns-2 md:grid-cols-4 lg:px-8 lg:pb-8 lg:pt-3 ${
                position === 'fixed'
                  ? 'bg-white'
                  : 'bg-off-grey md:bg-rhubarb-lite'
              }`}
            >
              <div className="order-1 col-span-1 flex items-center md:order-none md:col-span-2 lg:col-span-1">
                <div className=" flex w-full flex-row items-center md:w-auto md:justify-end md:border-b-0">
                  <span className=" font-interMedium text-sm leading-5 text-black md:text-base md:leading-4 lg:text-lg lg:leading-5 ">
                    Daily Calorie Intake:
                    <span className="pl-1 font-interBold text-rhubarb-dark">
                      {cal}
                    </span>
                    <span className="pl-1 font-interMedium text-black">
                      CAL
                    </span>
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div
              className={`rounded-b-xl px-3 py-1 lg:px-8 lg:py-4 ${
                position === 'fixed' ? 'bg-white' : 'bg-rhubarb-lite'
              }`}
            ></div>
          )}
        </div>
      ) : (
        <div
          className={`grid grid-cols-1 items-center rounded-xl p-3 px-5 md:columns-2 md:grid-cols-4 md:pb-0 lg:px-8 lg:py-5 ${
            position === 'fixed' ? 'bg-white' : 'bg-rhubarb-lite'
          }`}
        >
          <div className="font-interMedium text-2xl capitalize leading-7 text-rhubarb-dark md:col-span-2 md:pb-0 lg:col-span-3 lg:text-black">
            My {planType} Plan
          </div>
        </div>
      )}
      {/* Important Info Modal */}
      <Modal
        isOpen={infoModal}
        onRequestClose={() => setInfoModal(false)}
        headerTitle={''}
      >
        <div className="flex flex-col items-center justify-center pb-6 pt-10 md:pb-8">
          <div>
            <Image
              width={imageDimensions}
              height={imageDimensions}
              alt={''}
              className="w-full"
              src="/assets/icons/Goals.svg"
            />
          </div>
          <div className="pt-6 font-interSemibold text-4xl leading-[34px] md:pt-8 md:font-interMedium md:text-5xl md:leading-[38px]">
            Hit Your Target!
          </div>
          <p className="px-6 pb-8 pt-6 text-center font-inter text-sm font-normal leading-[18px] md:px-12 md:font-interMedium md:text-lg md:leading-[22px]">
            You’ve got some spare calories to make up your recommended daily
            target. Go ahead and add an extra meal, snack or drink to each day.
          </p>
          <div
            onClick={() => setInfoModal(false)}
            className="w-[249px] rounded-lg border border-black bg-off-white py-[15px] text-center"
          >
            <button
              type="button"
              className="font-interMedium text-base leading-6"
            >
              Got it
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
});

export default memo(Header);
